/*
 * PKI.Asn1 — DER encoder / decoder.
 *
 * The foundation every certificate tool on this site stands on. Certificates,
 * CSRs, private keys and PFX bundles are all ASN.1 DER; WebCrypto can generate
 * and sign keys but cannot produce or read any of these container formats, so
 * the structures are built and parsed here by hand.
 *
 * Decoder is BER-tolerant on input (real-world files from Java keytool and old
 * OpenSSL are not always strictly DER) but the encoder emits canonical DER —
 * anything we hand back must verify in openssl, Windows and Java.
 *
 * UMD-lite footer so the Node regression harness tests THIS shipped code.
 */
(function (root) {
    'use strict';

    /* ─── tag constants ─────────────────────────────────────────────── */

    var TAG = {
        BOOLEAN: 0x01, INTEGER: 0x02, BIT_STRING: 0x03, OCTET_STRING: 0x04,
        NULL: 0x05, OID: 0x06, ENUMERATED: 0x0A, UTF8_STRING: 0x0C,
        SEQUENCE: 0x30, SET: 0x31,
        NUMERIC_STRING: 0x12, PRINTABLE_STRING: 0x13, T61_STRING: 0x14,
        IA5_STRING: 0x16, UTC_TIME: 0x17, GENERALIZED_TIME: 0x18,
        VISIBLE_STRING: 0x1A, GENERAL_STRING: 0x1B, UNIVERSAL_STRING: 0x1C,
        BMP_STRING: 0x1E
    };

    // Tags whose content is text. Kept as a set so the decoder can surface a
    // readable `.text` without the caller having to know the alphabet zoo.
    var STRING_TAGS = [
        TAG.UTF8_STRING, TAG.NUMERIC_STRING, TAG.PRINTABLE_STRING, TAG.T61_STRING,
        TAG.IA5_STRING, TAG.VISIBLE_STRING, TAG.GENERAL_STRING,
        TAG.UNIVERSAL_STRING, TAG.BMP_STRING
    ];

    /* ─── byte helpers ──────────────────────────────────────────────── */

    function u8(x) {
        if (x instanceof Uint8Array) {
            // Node's Buffer subclasses Uint8Array but overrides slice() to
            // return a VIEW instead of a copy. Anything downstream that copies
            // via .slice() would silently alias the shared 8 KB allocation pool
            // — which is exactly how a PKCS#12 MAC ends up computed over 8192
            // bytes of neighbouring memory instead of the file. Re-wrap as a
            // plain Uint8Array so every method has its standard semantics.
            return x.constructor === Uint8Array ? x : new Uint8Array(x.buffer, x.byteOffset, x.byteLength);
        }
        if (Array.isArray(x)) return new Uint8Array(x);
        if (x instanceof ArrayBuffer) return new Uint8Array(x);
        if (x && x.buffer instanceof ArrayBuffer) return new Uint8Array(x.buffer, x.byteOffset, x.byteLength);
        throw new TypeError('expected bytes');
    }

    function concat(parts) {
        var total = 0, i;
        for (i = 0; i < parts.length; i++) total += parts[i].length;
        var out = new Uint8Array(total), off = 0;
        for (i = 0; i < parts.length; i++) { out.set(parts[i], off); off += parts[i].length; }
        return out;
    }

    function bytesEqual(a, b) {
        if (a.length !== b.length) return false;
        for (var i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
        return true;
    }

    // Constant-time-ish compare is pointless here (all data is public or
    // user-owned and local), so plain lexicographic is fine — it exists to give
    // DER SET OF its canonical ordering.
    function compareBytes(a, b) {
        var n = Math.min(a.length, b.length);
        for (var i = 0; i < n; i++) { if (a[i] !== b[i]) return a[i] - b[i]; }
        return a.length - b.length;
    }

    function toHex(bytes, sep) {
        var s = '', b = u8(bytes);
        for (var i = 0; i < b.length; i++) {
            if (sep && i) s += sep;
            s += (b[i] < 16 ? '0' : '') + b[i].toString(16);
        }
        return s;
    }

    function fromHex(hex) {
        var clean = String(hex).replace(/[^0-9a-fA-F]/g, '');
        if (clean.length % 2) clean = '0' + clean;
        var out = new Uint8Array(clean.length / 2);
        for (var i = 0; i < out.length; i++) out[i] = parseInt(clean.substr(i * 2, 2), 16);
        return out;
    }

    /* ─── length codec ──────────────────────────────────────────────── */

    function encodeLength(len) {
        if (len < 0x80) return new Uint8Array([len]);
        var tmp = [];
        while (len > 0) { tmp.unshift(len & 0xff); len = Math.floor(len / 256); }
        if (tmp.length > 126) throw new RangeError('length too large');
        return new Uint8Array([0x80 | tmp.length].concat(tmp));
    }

    /* ─── decoder ───────────────────────────────────────────────────── */

    /**
     * Parse one TLV at `offset`. Returns a node; constructed nodes carry
     * `.children`. Indefinite-length (BER) constructed values are supported
     * because Java and some HSM exports emit them, but we never produce them.
     */
    function parseOne(bytes, offset, depth) {
        var b = u8(bytes);
        offset = offset || 0;
        depth = depth || 0;
        if (depth > 60) throw new Error('ASN.1 nesting too deep');
        if (offset >= b.length) throw new Error('ASN.1 truncated: no tag at ' + offset);

        var start = offset;
        var first = b[offset++];
        var cls = (first & 0xc0) >> 6;
        var constructed = !!(first & 0x20);
        var tagNumber = first & 0x1f;

        if (tagNumber === 0x1f) { // high-tag-number form
            tagNumber = 0;
            var guard = 0;
            do {
                if (offset >= b.length) throw new Error('ASN.1 truncated in tag');
                if (++guard > 6) throw new Error('ASN.1 tag number too large');
                tagNumber = (tagNumber * 128) + (b[offset] & 0x7f);
            } while (b[offset++] & 0x80);
        }

        if (offset >= b.length) throw new Error('ASN.1 truncated: no length at ' + offset);
        var lenByte = b[offset++], length, indefinite = false;

        if (lenByte === 0x80) {
            if (!constructed) throw new Error('ASN.1 indefinite length on primitive');
            indefinite = true;
            length = -1;
        } else if (lenByte & 0x80) {
            var n = lenByte & 0x7f;
            if (n > 6) throw new Error('ASN.1 length field too large');
            if (offset + n > b.length) throw new Error('ASN.1 truncated in length');
            length = 0;
            for (var i = 0; i < n; i++) length = (length * 256) + b[offset++];
        } else {
            length = lenByte;
        }

        var headerLen = offset - start;
        var node = {
            tag: first, cls: cls, constructed: constructed, tagNumber: tagNumber,
            start: start, headerLen: headerLen, indefinite: indefinite,
            length: 0, end: 0, bytes: null, children: null
        };

        if (indefinite) {
            // Read children until end-of-contents (00 00).
            var kids = [], cursor = offset;
            for (;;) {
                if (cursor + 1 >= b.length) throw new Error('ASN.1 unterminated indefinite length');
                if (b[cursor] === 0x00 && b[cursor + 1] === 0x00) { cursor += 2; break; }
                var kid = parseOne(b, cursor, depth + 1);
                kids.push(kid);
                cursor = kid.end;
            }
            node.children = kids;
            node.end = cursor;
            node.length = cursor - 2 - offset;
            node.bytes = b.subarray(offset, cursor - 2);
        } else {
            if (offset + length > b.length) {
                throw new Error('ASN.1 truncated: declared ' + length + ' bytes at offset ' +
                    start + ', only ' + (b.length - offset) + ' available');
            }
            node.length = length;
            node.end = offset + length;
            node.bytes = b.subarray(offset, node.end);
            if (constructed) {
                var out = [], c = offset;
                while (c < node.end) {
                    var child = parseOne(b, c, depth + 1);
                    out.push(child);
                    if (child.end <= c) throw new Error('ASN.1 zero-advance');
                    c = child.end;
                }
                node.children = out;
            }
        }

        node.full = b.subarray(node.start, node.end);
        decorate(node);
        return node;
    }

    // Attach convenience readers so callers read `.oid` / `.text` / `.int`
    // instead of re-deriving them at every use site.
    function decorate(node) {
        var t = node.tag;
        if (node.cls !== 0) return; // context/application tags carry no universal meaning
        if (t === TAG.OID) {
            try { node.oid = decodeOid(node.bytes); } catch (e) { node.oid = null; }
        } else if (STRING_TAGS.indexOf(t) >= 0) {
            node.text = decodeString(t, node.bytes);
        } else if (t === TAG.INTEGER || t === TAG.ENUMERATED) {
            node.intHex = toHex(node.bytes);
        } else if (t === TAG.BOOLEAN) {
            node.bool = node.bytes.length > 0 && node.bytes[0] !== 0;
        } else if (t === TAG.BIT_STRING) {
            node.unusedBits = node.bytes.length ? node.bytes[0] : 0;
            node.bits = node.bytes.subarray(1);
        } else if (t === TAG.UTC_TIME || t === TAG.GENERALIZED_TIME) {
            node.date = decodeTime(t, node.bytes);
        }
    }

    /** Parse the first TLV and require it to consume the whole buffer. */
    function parse(bytes) {
        var b = u8(bytes);
        var node = parseOne(b, 0, 0);
        if (node.end !== b.length) {
            // Trailing bytes usually mean the user pasted two structures or the
            // file is a concatenated bag; surface it rather than silently ignore.
            node.trailingBytes = b.subarray(node.end);
        }
        return node;
    }

    /** Parse a buffer holding one or more concatenated TLVs. */
    function parseAll(bytes) {
        var b = u8(bytes), out = [], c = 0;
        while (c < b.length) {
            // Tolerate trailing NUL/whitespace padding some exporters add.
            if (b[c] === 0x00 && c + 1 >= b.length) break;
            var node = parseOne(b, c, 0);
            out.push(node);
            c = node.end;
        }
        return out;
    }

    function decodeOid(bytes) {
        var b = u8(bytes);
        if (!b.length) throw new Error('empty OID');
        var parts = [], first = b[0];
        // X.690: first octet packs the two top arcs. Arc 2 is unbounded, hence
        // the first<80 branch rather than a flat divide.
        if (first < 40) parts.push(0, first);
        else if (first < 80) parts.push(1, first - 40);
        else parts.push(2, first - 80);

        var value = 0, started = false;
        for (var i = 1; i < b.length; i++) {
            var byte = b[i];
            // Values above 2^53 would lose precision in a Number; OIDs that large
            // do not occur in PKI, but guard so we fail loudly instead of lying.
            value = value * 128 + (byte & 0x7f);
            if (!Number.isSafeInteger(value)) throw new Error('OID arc too large');
            started = true;
            if (!(byte & 0x80)) { parts.push(value); value = 0; started = false; }
        }
        if (started) throw new Error('OID truncated');
        return parts.join('.');
    }

    function encodeOid(dotted) {
        var parts = String(dotted).split('.').map(Number);
        if (parts.length < 2 || parts.some(function (p) { return !Number.isInteger(p) || p < 0; })) {
            throw new Error('invalid OID: ' + dotted);
        }
        var body = [parts[0] * 40 + parts[1]];
        for (var i = 2; i < parts.length; i++) {
            var v = parts[i], stack = [v & 0x7f];
            v = Math.floor(v / 128);
            while (v > 0) { stack.unshift((v & 0x7f) | 0x80); v = Math.floor(v / 128); }
            body = body.concat(stack);
        }
        return tlv(TAG.OID, new Uint8Array(body));
    }

    function decodeString(tag, bytes) {
        var b = u8(bytes);
        if (tag === TAG.BMP_STRING) {
            // UCS-2 big-endian.
            var s = '';
            for (var i = 0; i + 1 < b.length; i += 2) s += String.fromCharCode((b[i] << 8) | b[i + 1]);
            return s;
        }
        if (tag === TAG.UNIVERSAL_STRING) {
            var u = '';
            for (var j = 0; j + 3 < b.length; j += 4) {
                u += String.fromCodePoint((b[j] << 24 >>> 0) + (b[j + 1] << 16) + (b[j + 2] << 8) + b[j + 3]);
            }
            return u;
        }
        if (tag === TAG.T61_STRING) {
            // TeletexString in the wild is almost always Latin-1 (or mislabelled
            // UTF-8). Try UTF-8 first, fall back to Latin-1 when it is invalid.
            try {
                return new TextDecoder('utf-8', { fatal: true }).decode(b);
            } catch (e) {
                var l = '';
                for (var k = 0; k < b.length; k++) l += String.fromCharCode(b[k]);
                return l;
            }
        }
        try { return new TextDecoder('utf-8').decode(b); }
        catch (e2) {
            var f = '';
            for (var m = 0; m < b.length; m++) f += String.fromCharCode(b[m]);
            return f;
        }
    }

    function decodeTime(tag, bytes) {
        var s = decodeString(TAG.IA5_STRING, bytes).trim();
        var m;
        if (tag === TAG.UTC_TIME) {
            // YYMMDDHHMM[SS](Z|±hhmm)
            m = /^(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})?(Z|[+-]\d{4})?$/.exec(s);
            if (!m) return null;
            var yy = parseInt(m[1], 10);
            // RFC 5280: 00-49 => 20xx, 50-99 => 19xx.
            var year = yy < 50 ? 2000 + yy : 1900 + yy;
            return buildDate(year, m[2], m[3], m[4], m[5], m[6], m[7]);
        }
        // GeneralizedTime: YYYYMMDDHHMMSS[.fff](Z|±hhmm)
        m = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})?(\d{2})?(?:\.\d+)?(Z|[+-]\d{4})?$/.exec(s);
        if (!m) return null;
        return buildDate(parseInt(m[1], 10), m[2], m[3], m[4], m[5] || '00', m[6], m[7]);
    }

    function buildDate(year, mo, d, h, mi, sec, zone) {
        var ms = Date.UTC(year, parseInt(mo, 10) - 1, parseInt(d, 10),
            parseInt(h, 10), parseInt(mi, 10), sec ? parseInt(sec, 10) : 0);
        if (zone && zone !== 'Z') {
            var sign = zone[0] === '-' ? 1 : -1; // offset is FROM local TO UTC
            ms += sign * (parseInt(zone.substr(1, 2), 10) * 3600 + parseInt(zone.substr(3, 2), 10) * 60) * 1000;
        }
        return new Date(ms);
    }

    /* ─── encoder ───────────────────────────────────────────────────── */

    function tlv(tag, content) {
        var c = u8(content);
        return concat([new Uint8Array([tag]), encodeLength(c.length), c]);
    }

    function sequence() {
        var parts = flatten(arguments);
        return tlv(TAG.SEQUENCE, concat(parts));
    }

    function set() {
        var parts = flatten(arguments);
        return tlv(TAG.SET, concat(parts));
    }

    /** DER SET OF: elements sorted by their own encoding (X.690 §11.6). */
    function setOf() {
        var parts = flatten(arguments).slice().sort(compareBytes);
        return tlv(TAG.SET, concat(parts));
    }

    function flatten(args) {
        var out = [];
        for (var i = 0; i < args.length; i++) {
            var a = args[i];
            if (Array.isArray(a)) out = out.concat(flatten(a));
            else if (a) out.push(u8(a));
        }
        return out;
    }

    /** INTEGER from big-endian magnitude bytes (non-negative values only). */
    function integer(magnitude) {
        var b = u8(magnitude), i = 0;
        while (i < b.length - 1 && b[i] === 0x00) i++; // strip leading zeros
        var body = b.subarray(i);
        if (!body.length) body = new Uint8Array([0]);
        // A leading bit of 1 would read as negative — DER requires a 0x00 pad.
        if (body[0] & 0x80) body = concat([new Uint8Array([0]), body]);
        return tlv(TAG.INTEGER, body);
    }

    function integerFromNumber(n) {
        if (!Number.isSafeInteger(n) || n < 0) throw new RangeError('integerFromNumber: ' + n);
        if (n === 0) return tlv(TAG.INTEGER, new Uint8Array([0]));
        var stack = [];
        while (n > 0) { stack.unshift(n & 0xff); n = Math.floor(n / 256); }
        return integer(new Uint8Array(stack));
    }

    function bitString(content, unusedBits) {
        return tlv(TAG.BIT_STRING, concat([new Uint8Array([unusedBits || 0]), u8(content)]));
    }

    /** BIT STRING carrying a named-bits flag set (KeyUsage and friends). */
    function namedBits(bitIndexes) {
        if (!bitIndexes.length) return bitString(new Uint8Array(0), 0);
        var max = Math.max.apply(null, bitIndexes);
        var bytes = new Uint8Array(Math.floor(max / 8) + 1);
        bitIndexes.forEach(function (idx) { bytes[Math.floor(idx / 8)] |= 0x80 >> (idx % 8); });
        // DER: trailing zero bits are not encoded, so unused = pad to the last set bit.
        return bitString(bytes, 7 - (max % 8));
    }

    function octetString(content) { return tlv(TAG.OCTET_STRING, content); }
    function nullValue() { return new Uint8Array([TAG.NULL, 0x00]); }
    function boolean(v) { return tlv(TAG.BOOLEAN, new Uint8Array([v ? 0xff : 0x00])); }

    function utf8String(s) { return tlv(TAG.UTF8_STRING, new TextEncoder().encode(s)); }
    function ia5String(s) { return tlv(TAG.IA5_STRING, latin1(s)); }
    function printableString(s) { return tlv(TAG.PRINTABLE_STRING, latin1(s)); }
    function bmpString(s) {
        var out = new Uint8Array(s.length * 2);
        for (var i = 0; i < s.length; i++) {
            var c = s.charCodeAt(i);
            out[i * 2] = c >> 8; out[i * 2 + 1] = c & 0xff;
        }
        return tlv(TAG.BMP_STRING, out);
    }

    function latin1(s) {
        var out = new Uint8Array(s.length);
        for (var i = 0; i < s.length; i++) out[i] = s.charCodeAt(i) & 0xff;
        return out;
    }

    // PrintableString alphabet per X.680. Anything outside it must go out as
    // UTF8String or the result will not parse in strict validators.
    var PRINTABLE_RE = /^[A-Za-z0-9 '()+,\-./:=?]*$/;

    /** Pick the narrowest legal string type, the way OpenSSL does. */
    function directoryString(s) {
        return PRINTABLE_RE.test(s) ? printableString(s) : utf8String(s);
    }

    function utcTime(date) {
        var d = date instanceof Date ? date : new Date(date);
        var s = two(d.getUTCFullYear() % 100) + two(d.getUTCMonth() + 1) + two(d.getUTCDate()) +
            two(d.getUTCHours()) + two(d.getUTCMinutes()) + two(d.getUTCSeconds()) + 'Z';
        return tlv(TAG.UTC_TIME, latin1(s));
    }

    function generalizedTime(date) {
        var d = date instanceof Date ? date : new Date(date);
        var s = String(d.getUTCFullYear()) + two(d.getUTCMonth() + 1) + two(d.getUTCDate()) +
            two(d.getUTCHours()) + two(d.getUTCMinutes()) + two(d.getUTCSeconds()) + 'Z';
        return tlv(TAG.GENERALIZED_TIME, latin1(s));
    }

    /**
     * RFC 5280 §4.1.2.5: dates through 2049 use UTCTime, 2050+ GeneralizedTime.
     * Getting this wrong is a classic source of "openssl parses it, Windows does
     * not" — so it is centralised here rather than chosen at each call site.
     */
    function time(date) {
        var d = date instanceof Date ? date : new Date(date);
        return d.getUTCFullYear() >= 2050 ? generalizedTime(d) : utcTime(d);
    }

    function two(n) { return (n < 10 ? '0' : '') + n; }

    /** Context-specific tag [n]. `constructed` defaults to true. */
    function context(n, content, constructed) {
        var isC = constructed === undefined ? true : !!constructed;
        return tlv(0x80 | (isC ? 0x20 : 0x00) | n, content);
    }

    /** EXPLICIT [n] wrapper around an already-encoded value. */
    function explicit(n, encoded) { return context(n, encoded, true); }

    /** IMPLICIT [n] — replaces the tag of an already-encoded primitive value. */
    function implicit(n, encoded, constructed) {
        var node = parse(encoded);
        return context(n, node.bytes, constructed === undefined ? node.constructed : constructed);
    }

    /* ─── navigation helpers ────────────────────────────────────────── */

    /** node.children[i], or null — keeps optional-field walking readable. */
    function at(node, i) {
        return node && node.children && node.children[i] ? node.children[i] : null;
    }

    /** Walk a path of child indexes: get(root, 0, 1, 0). */
    function get(node) {
        var cur = node;
        for (var i = 1; i < arguments.length; i++) {
            cur = at(cur, arguments[i]);
            if (!cur) return null;
        }
        return cur;
    }

    /** First child with a context-specific tag number `n`. */
    function contextChild(node, n) {
        if (!node || !node.children) return null;
        for (var i = 0; i < node.children.length; i++) {
            var c = node.children[i];
            if (c.cls === 2 && c.tagNumber === n) return c;
        }
        return null;
    }

    var API = {
        TAG: TAG,
        // decode
        parse: parse, parseAll: parseAll, parseOne: parseOne,
        decodeOid: decodeOid, decodeString: decodeString, decodeTime: decodeTime,
        // encode
        tlv: tlv, sequence: sequence, set: set, setOf: setOf,
        integer: integer, integerFromNumber: integerFromNumber,
        bitString: bitString, namedBits: namedBits, octetString: octetString,
        nullValue: nullValue, boolean: boolean, oid: encodeOid,
        utf8String: utf8String, ia5String: ia5String, printableString: printableString,
        bmpString: bmpString, directoryString: directoryString,
        utcTime: utcTime, generalizedTime: generalizedTime, time: time,
        context: context, explicit: explicit, implicit: implicit,
        // navigate
        at: at, get: get, contextChild: contextChild,
        // bytes
        u8: u8, concat: concat, toHex: toHex, fromHex: fromHex,
        bytesEqual: bytesEqual, compareBytes: compareBytes
    };

    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    if (root) { root.PKI = root.PKI || {}; root.PKI.Asn1 = API; }
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
